/**
 * The totals row under a register.
 *
 * One rule governs this component, and it is the reason the API computes these rather than
 * the browser: **the totals cover every matching row, not the page on screen.** A footer
 * that summed the visible twenty-five and called it the total would be the most dangerous
 * number in the product — somebody reads it into a board pack, and it is wrong by a factor
 * of however many pages there were.
 *
 * So each cell shows the figure *and* how many rows carry a value. A sum over 12 of 1,291
 * rows and a sum over all 1,291 look identical otherwise, and they mean very different
 * things.
 */
import { useTranslation } from 'react-i18next'

import type { ColumnInfo } from '@/api/types'
import { formatMoney } from '@/lib/format'

export type ColumnTotal = {
  column: string
  kind: string
  sum: number | null
  avg: number | null
  min: number | null
  max: number | null
  count: number
}

export type Totals = {
  total_rows: number
  columns: ColumnTotal[]
}

/** Which measure actually means something for a column of this kind. */
function measure(kind: string): 'sum' | 'avg' {
  // Summing a percentage gives a number with no meaning — 340% complete across four
  // packages is not a fact about anything. Averaging money hides the total, which is the
  // one figure a finance reviewer opens the register for.
  return kind === 'percent' ? 'avg' : 'sum'
}

export function GridTotals({
  columns,
  totals,
  locale,
}: {
  columns: ColumnInfo[]
  totals: Totals | undefined
  locale: string
}) {
  const { t } = useTranslation()
  if (!totals) return null

  const byColumn = new Map(totals.columns.map((entry) => [entry.column, entry]))
  const anything = columns.some((column) => byColumn.has(column.name))
  if (!anything) return null

  return (
    <tfoot className="totals" data-testid="grid-totals">
      <tr>
        {columns.map((column, index) => {
          const total = byColumn.get(column.name)

          if (index === 0) {
            return (
              <th key={column.name} scope="row" className="totals__label">
                {t('grid.totalsOver', { count: totals.total_rows })}
              </th>
            )
          }

          if (!total) return <td key={column.name} />

          const which = measure(column.kind)
          const value = which === 'avg' ? total.avg : total.sum

          return (
            <td key={column.name} className="totals__cell numeric" data-testid="grid-total">
              <span className="totals__value">
                {value === null
                  ? '—'
                  : column.kind === 'money'
                    ? formatMoney(value, locale)
                    : format(value, locale, column.kind)}
              </span>
              <span className="totals__meta">
                {/* Named, because "1,432" alone does not say whether it is a sum or a mean,
                    and the two are not interchangeable to anyone reading a register. */}
                {t(which === 'avg' ? 'grid.mean' : 'grid.sum')}
                {total.count < totals.total_rows ? (
                  <> · {t('grid.ofRows', { count: total.count })}</>
                ) : null}
              </span>
            </td>
          )
        })}
      </tr>
    </tfoot>
  )
}

function format(value: number, locale: string, kind: string): string {
  if (kind === 'percent') {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      maximumFractionDigits: 1,
    }).format(value / 100)
  }
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: kind === 'integer' ? 0 : 2,
  }).format(value)
}
